// 레이드 4인 넷 모드 클라 연결층(docs/RAID-PRD.md 0장 2단계, src/server/raid.ts RaidRoom). 게임·렌더와 분리된 네트워크 층 — partyNet.ts(PartyClient)를 본떴다.
// - 연결: 같은 출처 /api/raid/ws?code=new|<코드>&raid=raid_wyvern (wss). 끊기면 지수 백오프(0.5 → 8초)로 같은 코드에 다시 붙는다.
//   서버가 자리를 90초 유지하고, 다시 붙어 hello를 보내면 go + sync(보스·경고·불 장판·칸 전부)를 다시 준다.
// - 로비: room(명단·준비·단계·용병 계획·자동 충원 시각), 보내기 ready/start/leave.
// - 판: go → sync → s(10Hz 스냅)/ev(사건) → end. 보스·칸은 스냅 두 개 사이를 100ms 뒤 시각으로 보간한다(performance.now 시계).
// - 서버 틱 시계: 모든 시뮬 메시지(sync·s·ev)의 틱 k와 받은 시각으로 지금 서버 틱을 추정한다(serverTick). 경고의 진행·판정 틱이 이것으로 돈다.
// - 게임 연결(src/game/raidLink.ts)은 매 틱 update(x, z, f, act)로 내 위치를 12Hz로 보내고, hit(d)·down()을 부르고, drain()으로 사건을 받는다.
// 오프라인·서버 오류면 예외를 던지지 않는다(error 문자열만).
import {
  RAID_PROTO_VERSION, RAID_WS_PATH,
  type RaidClientMsg, type RaidEvent, type RaidRoomView, type RaidServerMsg, type RaidSnapBoss, type RaidSnapSlot, type RaidSync,
} from '../shared/raidNet';
import type { RaidId } from '../shared/content/raid';
import type { HeroId } from '../shared/content/types';

export interface RaidSocketLike {
  readyState: number;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason?: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}
export type RaidStatus = 'idle' | 'connecting' | 'online' | 'retrying' | 'closed';

export interface RaidClientOptions {
  name: string;
  hero: HeroId;
  lv: number;
  raid?: RaidId;
  /** 접속 주소 직접 지정(QA 서버, ws://…/api/raid/ws). 없으면 같은 출처 */
  url?: string;
  now?: () => number;
  socket?: (url: string) => RaidSocketLike;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (id: unknown) => void;
}

/** 보간 대상 하나(보스·칸). y는 보스 비행 높이(0..1)를 같이 보간한다 */
export interface RaidTrack {
  id: number;
  /** 칸 종류(1 사람, 10+n 용병 MERCS 번호 n), 동작(RaidAct), 체력, 최대 체력, 쓰러짐 남은 틱 */
  kind: number; act: number; hp: number; max: number; down: number;
  ax: number; az: number; af: number; ay: number; at: number;
  bx: number; bz: number; bf: number; by: number; bt: number;
  /** 보간한 지금 값(sample()이 채운다) */
  x: number; z: number; f: number; y: number;
  seen: boolean;
}
/** 보스의 보간하지 않는 값(마지막 스냅) */
export interface RaidBossInfo {
  hp: number; maxHp: number; state: number; phase: number; enraged: boolean; stagger: number;
  /** 표적 칸·도발 칸(서버 칸 번호, -1 없음), 광폭까지 남은 틱 */
  target: number; taunt: number; enrageIn: number;
  /** 마지막 스냅의 서버 틱 */
  k: number;
}

export type RaidNetEvent =
  | { t: 'go'; runId: string; at: number; you: number; mercs: number[]; bossHp: number; again: boolean; /** 난이도(C13, 옛 서버 = 노말) */ diff: 'normal' | 'hard' }
  | { t: 'sync'; s: RaidSync }
  | { t: 'ev'; k: number; e: RaidEvent }
  | { t: 'correct'; x: number; z: number }
  | Extract<RaidServerMsg, { t: 'end' }>;

/** 보간 지연(스냅 10Hz 한 칸) */
export const RAID_INTERP_MS = 100;
/** 위치 송신 주기(12Hz) */
export const RAID_POS_MS = 1000 / 12;
export const RAID_KEEPALIVE_MS = 25_000;
/** 서버 시뮬 한 틱(ms) */
export const SIM_TICK_MS = 1000 / 60;
const MAX_EVENTS = 512;

function track(id: number): RaidTrack {
  return { id, kind: 0, act: 0, hp: 0, max: 1, down: 0, ax: 0, az: 0, af: 0, ay: 0, at: 0, bx: 0, bz: 0, bf: 0, by: 0, bt: 0, x: 0, z: 0, f: 0, y: 0, seen: false };
}
function lerpAngle(a: number, b: number, k: number): number {
  // 반복문 대신 나머지로 접는다(서버가 보낸 값이 아주 커도 멈추지 않게)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return Number.isFinite(b) ? b : 0;
  const tau = Math.PI * 2;
  let d = (b - a) % tau;
  if (d > Math.PI) d -= tau; else if (d < -Math.PI) d += tau;
  return a + d * k;
}

export class RaidClient {
  status: RaidStatus = 'idle';
  room: RaidRoomView | null = null;
  you = -1;
  error = '';
  /** 판 진행 중인지(go ~ end) */
  inRun = false;
  runId = '';
  startAt = 0;
  /** 판 칸별 용병(MERCS 번호, 사람 칸 -1) — go가 준다 */
  mercs: number[] = [];
  /** 첫 sync를 받았는가(보스·칸이 채워졌는가) */
  synced = false;
  readonly boss = track(-1);
  readonly bossInfo: RaidBossInfo = { hp: 0, maxHp: 1, state: 0, phase: 1, enraged: false, stagger: 0, target: -1, taunt: -1, enrageIn: 0, k: 0 };
  /** 칸 번호 → 보간 트랙(내 칸 포함 — 내 칸은 서버 체력·쓰러짐 확인용) */
  readonly slots = new Map<number, RaidTrack>();
  /** 마지막 판 결과(end) */
  /** 새 방을 만들 때 고른 난이도(C13) */
  private newDiff: 'normal' | 'hard' = 'normal';
  lastEnd: Extract<RaidServerMsg, { t: 'end' }> | null = null;
  /** 바뀔 때마다 오른다(UI가 다시 그릴지 판단) */
  revision = 0;
  /** 보낸 hit 수·pos 수(QA) */
  hitsSent = 0;
  posSent = 0;

  private ws: RaidSocketLike | null = null;
  private code = '';
  private wantOpen = false;
  private backoff = 500;
  private joinFails = 0;
  private helloSent: { name: string; hero: string; lv: number } | null = null;
  private retryTimer: unknown = null;
  private pingTimer: unknown = null;
  private lastSend = -1e9;
  private sentX = NaN; private sentZ = NaN; private sentF = NaN; private sentA = -1;
  private readonly events: RaidNetEvent[] = [];
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (id: unknown) => void;
  /** 서버 틱 시계: 마지막 메시지의 틱과 받은 시각, 지금까지 낸 가장 큰 추정값(되돌아가지 않게) */
  private clockK = 0; private clockAt = 0; private clockSet = false; private lastEst = 0;

  constructor(private readonly o: RaidClientOptions) {
    this.now = o.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
    this.setTimer = o.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = o.clearTimer ?? ((id) => clearTimeout(id as ReturnType<typeof setTimeout>));
  }

  /** 새 레이드 방 만들기 */
  /** 새 방(diff = 'hard'면 하드 방 — 서버가 1차 환생을 다시 확인한다, C13) */
  create(diff: 'normal' | 'hard' = 'normal'): void { this.newDiff = diff; this.open('new'); }
  /** 코드로 참가 */
  join(code: string): void { this.open(code.trim().toUpperCase()); }
  get raidCode(): string { return this.room?.code ?? (this.code !== 'new' ? this.code : ''); }
  get leader(): boolean { return !!this.room && this.room.members.some((m) => m.slot === this.you && m.leader); }

  private open(code: string): void {
    // 명시적 방 만들기·참가는 이전 방·판 기억을 비운다(자동 재접속에서만 유지) — 안 비우면 실패한 새 코드 대신 옛 방 코드로 재시도한다
    this.close(true);
    this.code = code; this.wantOpen = true; this.error = ''; this.backoff = 500; this.joinFails = 0;
    this.connect();
  }

  /** 방에서 나간다(서버에 leave를 보내고 닫는다) */
  leave(): void {
    this.send({ t: 'leave' });
    this.close(true);
  }

  /** 연결만 닫는다(leave 없이 — 페이지를 떠날 때). reset이면 방 정보도 지운다 */
  close(reset = true): void {
    this.wantOpen = false;
    if (this.retryTimer !== null) { this.clearTimer(this.retryTimer); this.retryTimer = null; }
    if (this.pingTimer !== null) { this.clearTimer(this.pingTimer); this.pingTimer = null; }
    const ws = this.ws;
    this.ws = null;
    if (ws) { ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null; try { ws.close(1000, 'bye'); } catch { /* 이미 닫힘 */ } }
    this.status = 'closed';
    if (reset) { this.room = null; this.you = -1; this.endRun(); this.code = ''; this.helloSent = null; }
    this.revision++;
  }

  private endRun(): void {
    this.inRun = false; this.synced = false; this.slots.clear(); this.boss.seen = false; this.mercs = [];
  }

  private url(): string {
    const base = this.o.url ?? (typeof location !== 'undefined' ? (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + RAID_WS_PATH : RAID_WS_PATH);
    return base + (base.includes('?') ? '&' : '?') + 'code=' + encodeURIComponent(this.code) + '&raid=' + encodeURIComponent(this.o.raid ?? 'raid_wyvern') + (this.code === 'new' && this.newDiff === 'hard' ? '&diff=hard' : '');
  }

  private connect(): void {
    this.status = this.status === 'retrying' ? 'retrying' : 'connecting';
    let ws: RaidSocketLike;
    try { ws = this.o.socket ? this.o.socket(this.url()) : (new WebSocket(this.url()) as unknown as RaidSocketLike); }
    catch { this.retry(); return; }
    this.ws = ws;
    ws.onopen = () => {
      this.status = 'online'; this.backoff = 500; this.revision++;
      this.sendHello();
      this.keepalive();
    };
    ws.onmessage = (ev) => { if (typeof ev.data === 'string') this.onMessage(ev.data); };
    ws.onerror = () => { /* onclose가 뒤따른다 */ };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = null;
      // 서버가 거절한 이유가 있는 닫힘(교체·세션 없음·나감·버전·추방)은 다시 붙지 않는다
      if (ev.code === 4000 || ev.code === 4001 || ev.code === 4002 || ev.code === 4003 || ev.code === 4005 || ev.code === 1000) {
        this.status = 'closed'; this.wantOpen = false;
        if (!this.error) this.error = ev.code === 4000 ? 'replaced' : ev.code === 4003 ? 'version' : ev.code === 4005 ? 'kicked' : ev.code === 4001 ? 'session' : '';
        this.revision++;
        return;
      }
      this.retry();
    };
  }

  private retry(): void {
    if (!this.wantOpen) return;
    // 아직 코드를 받지 못한 새 방(new)은 다시 만들지 않는다 — 처음부터 다시 누르게 한다
    if (this.code === 'new' && !this.room) { this.status = 'closed'; this.error = this.error || 'connect'; this.wantOpen = false; this.revision++; return; }
    // 아직 방에 들어간 적 없는 코드 참가(없는 코드·가득 찬 방·판 중인 방은 업그레이드 전에 404/409로 끊긴다): 3번 실패하면 멈추고 다시 입력하게 한다
    if (!this.room && ++this.joinFails >= 3) { this.status = 'closed'; this.error = this.error || 'join-failed'; this.wantOpen = false; this.revision++; return; }
    if (this.room) this.code = this.room.code;
    this.status = 'retrying'; this.revision++;
    const wait = this.backoff * (0.8 + Math.random() * 0.4);
    this.backoff = Math.min(8000, this.backoff * 2);
    this.retryTimer = this.setTimer(() => { this.retryTimer = null; if (this.wantOpen) this.connect(); }, wait);
  }

  private keepalive(): void {
    if (this.pingTimer !== null) this.clearTimer(this.pingTimer);
    this.pingTimer = this.setTimer(() => {
      this.pingTimer = null;
      if (this.ws && this.status === 'online') { try { this.ws.send('ping'); } catch { /* 닫히는 중 */ } this.keepalive(); }
    }, RAID_KEEPALIVE_MS);
  }

  private send(msg: RaidClientMsg): void {
    const ws = this.ws;
    if (!ws || this.status !== 'online') return;
    try { ws.send(JSON.stringify(msg)); } catch { /* 닫히는 중 */ }
  }

  /** 로비에 있는 동안 주기적으로(RaidNetLink): 영웅·레벨이 바뀌었으면 hello를 다시 알린다 */
  syncProfile(): void {
    if (this.status === 'online' && this.room && this.room.phase === 'lobby') this.syncHello();
  }
  private syncHello(): void {
    const h = this.helloSent;
    if (!h || h.hero !== this.o.hero || h.lv !== this.o.lv || h.name !== this.o.name) this.sendHello();
  }
  private sendHello(): void {
    const name = this.o.name, hero = this.o.hero, lv = this.o.lv;
    this.send({ t: 'hello', v: RAID_PROTO_VERSION, name, hero, lv });
    this.helloSent = { name, hero, lv };
  }
  ready(on: boolean): void { if (on) this.syncHello(); this.send({ t: 'ready', on }); }
  start(): void { this.syncHello(); this.send({ t: 'start' }); }
  /** 레이드 장면을 다 읽었다(판 시작 장벽 — 모두 보내거나 서버 제한 시간이 지나야 보스가 움직인다). 서버는 중복을 무시한다 */
  loaded(): void { this.send({ t: 'loaded' }); }
  /** 보스 타격(피해량 — 서버가 자른다) */
  hit(d: number): void {
    if (!this.inRun || !(d >= 1)) return;
    this.hitsSent++;
    this.send({ t: 'hit', d: Math.max(1, Math.round(d)) });
  }
  /** 내 영웅이 쓰러졌다(일으키기는 서버가 한다) */
  down(): void { if (this.inRun) this.send({ t: 'down' }); }

  /** 매 틱: 내 영웅 위치를 12Hz로(바뀐 것이 있을 때만) 보낸다. act는 0(서기·걷기) 또는 1(공격·기술 중) */
  update(x: number, z: number, facing: number, act: number): void {
    if (!this.inRun || this.status !== 'online' || !this.synced) return;
    const t = this.now();
    if (t - this.lastSend < RAID_POS_MS) return;
    if (Math.abs(x - this.sentX) < 0.02 && Math.abs(z - this.sentZ) < 0.02 && Math.abs(facing - this.sentF) < 0.05 && act === this.sentA) return;
    this.lastSend = t; this.sentX = x; this.sentZ = z; this.sentF = facing; this.sentA = act;
    this.posSent++;
    this.send({ t: 'pos', x: Math.round(x * 100) / 100, z: Math.round(z * 100) / 100, f: Math.round(facing * 100) / 100, a: act });
  }

  /** 쌓인 사건을 순서대로 넘기고 비운다(비었으면 아무것도 만들지 않는다) */
  drain(fn: (e: RaidNetEvent) => void): void {
    const n = this.events.length;
    if (n === 0) return;
    for (let i = 0; i < n; i++) fn(this.events[i]);
    if (this.events.length === n) this.events.length = 0; else this.events.splice(0, n);
  }

  // ── 서버 틱 시계 ───────────────────────────────────
  private clock(k: number): void {
    if (!Number.isFinite(k)) return;
    const t = this.now();
    // 서버가 저장해 둔 판을 이어 시계가 한참 되돌아갔다(DO 재시작): 되돌아가지 않는 규칙을 풀고 새로 맞춘다
    if (this.clockSet && k < this.lastEst - 120) this.lastEst = k;
    this.clockK = k; this.clockAt = t; this.clockSet = true;
  }
  /** 지금 서버 틱 추정(소수 틱). 마지막 메시지의 틱 + 받은 뒤 흐른 시간. 되돌아가지 않는다. 메시지를 받은 적이 없으면 0 */
  serverTick(): number {
    if (!this.clockSet) return 0;
    const est = this.clockK + (this.now() - this.clockAt) / SIM_TICK_MS;
    if (est > this.lastEst) this.lastEst = est;
    return this.lastEst;
  }

  // ── 보간 ───────────────────────────────────────────
  /** 보간: 지금 시각 − RAID_INTERP_MS 위치를 x·z·f·y에 채운다(보스·칸 모두) */
  sample(): void {
    const t = this.now() - RAID_INTERP_MS;
    this.lerp(this.boss, t);
    for (const p of this.slots.values()) this.lerp(p, t);
  }
  private lerp(k: RaidTrack, t: number): void {
    if (k.bt <= k.at) { k.x = k.bx; k.z = k.bz; k.f = k.bf; k.y = k.by; return; }
    const r = Math.max(0, Math.min(1, (t - k.at) / (k.bt - k.at)));
    k.x = k.ax + (k.bx - k.ax) * r; k.z = k.az + (k.bz - k.az) * r; k.f = lerpAngle(k.af, k.bf, r); k.y = k.ay + (k.by - k.ay) * r;
  }
  private push(k: RaidTrack, x: number, z: number, f: number, y: number, t: number): void {
    if (!k.seen) { k.ax = k.bx = x; k.az = k.bz = z; k.af = k.bf = f; k.ay = k.by = y; k.at = k.bt = t; k.x = x; k.z = z; k.f = f; k.y = y; k.seen = true; return; }
    // 보간 시작점 = 지금 보이는 위치(sample()을 한동안 안 불렀어도 낡은 위치에서 시작하지 않게 먼저 맞춘다)
    this.lerp(k, t - RAID_INTERP_MS);
    k.ax = k.x; k.az = k.z; k.af = k.f; k.ay = k.y; k.at = t - RAID_INTERP_MS;
    k.bx = x; k.bz = z; k.bf = f; k.by = y; k.bt = t;
  }
  private emit(e: RaidNetEvent): void {
    if (this.events.length >= MAX_EVENTS) this.events.shift();
    this.events.push(e);
  }

  private applyBoss(b: RaidSnapBoss, t: number): void {
    const i = this.bossInfo;
    i.hp = b[3]; i.maxHp = Math.max(1, b[4]); i.state = b[5]; i.phase = b[7]; i.enraged = b[8] === 1; i.stagger = b[9] / 100;
    i.target = b[10]; i.taunt = b[11]; i.enrageIn = b[12];
    this.push(this.boss, b[0], b[1], b[2], b[6] / 100, t);
  }
  private applySlots(p: readonly RaidSnapSlot[], t: number): void {
    // 스냅에 없는 칸(판 도중 나감)은 지운다 — 남으면 멈춘 유령으로 보인다
    for (const slot of this.slots.keys()) if (!p.some((s) => s[0] === slot)) this.slots.delete(slot);
    for (const s of p) {
      const k = this.slots.get(s[0]) ?? track(s[0]);
      k.kind = s[1]; k.act = s[5]; k.hp = s[6]; k.max = s[7]; k.down = s[8];
      this.push(k, s[2], s[3], s[4], 0, t);
      this.slots.set(s[0], k);
    }
  }

  /** 서버 메시지 처리(시험이 직접 부른다) */
  onMessage(text: string): void {
    if (text === 'pong') return;
    let msg: RaidServerMsg;
    try { msg = JSON.parse(text) as RaidServerMsg; } catch { return; }
    if (!msg || typeof msg !== 'object') return;
    const t = this.now();
    switch (msg.t) {
      case 'room':
        this.room = msg.room; this.you = msg.you; this.code = msg.room.code;
        break;
      case 'err':
        this.error = msg.code;
        break;
      case 'go': {
        const again = this.inRun && msg.runId === this.runId;
        this.you = msg.you; this.startAt = msg.at; this.mercs = Array.isArray(msg.mercs) ? msg.mercs.slice() : [];
        if (!again) {
          this.inRun = true; this.runId = msg.runId; this.lastEnd = null; this.synced = false;
          this.slots.clear(); this.boss.seen = false;
          this.clockSet = false; this.lastEst = 0;
          this.sentX = NaN;
        }
        this.emit({ t: 'go', runId: msg.runId, at: msg.at, you: msg.you, mercs: this.mercs, bossHp: msg.bossHp, again, diff: msg.diff === 'hard' ? 'hard' : 'normal' });
        break;
      }
      case 'sync':
        if (!this.inRun) break;
        this.clock(msg.s.tick);
        this.applyBoss(msg.s.boss, t);
        this.applySlots(msg.s.slots, t);
        this.bossInfo.k = msg.s.tick;
        this.synced = true; this.sentX = NaN;
        this.emit({ t: 'sync', s: msg.s });
        break;
      case 's':
        if (!this.inRun) break;
        this.clock(msg.k);
        this.applyBoss(msg.b, t);
        this.applySlots(msg.p, t);
        this.bossInfo.k = msg.k;
        this.synced = true;
        break;
      case 'ev':
        if (!this.inRun) break;
        this.clock(msg.k);
        for (const e of msg.ev) this.emit({ t: 'ev', k: msg.k, e });
        break;
      case 'correct': this.emit({ t: 'correct', x: msg.x, z: msg.z }); break;
      case 'end':
        // 앞 판의 늦은 결과(장부 재시도 뒤 도착)는 지금 판을 끝내지 않는다 — 결과만 남긴다
        if (this.inRun && msg.runId !== this.runId) { this.lastEnd = msg; break; }
        this.inRun = false; this.lastEnd = msg;
        this.slots.clear(); this.boss.seen = false; this.synced = false;
        this.emit(msg);
        break;
    }
    this.revision++;
  }
}
